import {
  FirestoreCollectionProsessorOptions,
  createFirestoreCollectionProsessor,
} from "../firebase/firestore";

import { appConnectMasterTriggerTypes } from "./AppConnectMasters";

export type ConnectStatus =
  | "start_connect"
  | "in_progress_connect"
  | "success_connect"
  | "failed_error"
  | "canceled_connect";

const cancelableConnectStatuses: ConnectStatus[] = [
  "start_connect",
  "in_progress_connect",
];

/**
 * @description workflow_execution_name が未設定のジョブはAPIが409を返すため対象外とする
 */
export const isCancelableConnectJobHistory = ({
  connectStatus,
  workflowExecutionName,
}: Pick<
  ConnectJobHistory,
  "connectStatus" | "workflowExecutionName"
>): boolean =>
  cancelableConnectStatuses.includes(connectStatus) && !!workflowExecutionName;

export type ConnectJobHistory = {
  id: string;
  connectId: string;
  appConnectMasterId: string;
  triggerType: (typeof appConnectMasterTriggerTypes)[number];
  connectJobId: string;
  connectInputBlob: string;
  connectOutputBlob: string;
  connectArtifactBlob: string;
  connectStatus: ConnectStatus;
  connectStatusDetail: string | null;
  connectStartedAt: string;
  connectFinishedAt: string;
  workflowExecutionName: string | null;
  // responseData: unknown;
};

type ConnectJobHistoryRaw = {
  id: string;
  connect_id: string;
  app_connect_master_id: string;
  trigger_type: (typeof appConnectMasterTriggerTypes)[number];
  connect_job_id: string;
  connect_input_blob: string;
  connect_output_blob: string;
  connect_artifact_blob: string;
  connect_status: ConnectStatus;
  connect_status_detail: string | null;
  connect_started_at: string;
  connect_finished_at: string;
  // 既存ドキュメントにはキー自体が存在しない
  workflow_execution_name?: string | null;
  // response_data: unknown;
};

const processorOptions: FirestoreCollectionProsessorOptions<
  ConnectJobHistory,
  ConnectJobHistoryRaw,
  Record<string, string>
> = {
  getCollectionPath: ({ organizationId, connectId }) =>
    `/app/spreadsheet_ui/v/1/organizations/${organizationId}/connects/${connectId}/job_histories`,
  toFirestore: ({
    id,
    connectId,
    appConnectMasterId,
    triggerType,
    connectJobId,
    connectInputBlob,
    connectOutputBlob,
    connectArtifactBlob,
    connectStatus,
    connectStatusDetail,
    connectStartedAt,
    connectFinishedAt,
    workflowExecutionName,
    // responseData,
  }) => ({
    id,
    connect_id: connectId,
    app_connect_master_id: appConnectMasterId,
    trigger_type: triggerType,
    connect_job_id: connectJobId,
    connect_input_blob: connectInputBlob,
    connect_output_blob: connectOutputBlob,
    connect_artifact_blob: connectArtifactBlob,
    connect_status: connectStatus,
    connect_status_detail: connectStatusDetail,
    connect_started_at: connectStartedAt,
    connect_finished_at: connectFinishedAt,
    workflow_execution_name: workflowExecutionName,
    // response_data: responseData,
  }),
  fromFirestore: ({
    id,
    connect_id,
    app_connect_master_id,
    trigger_type,
    connect_job_id,
    connect_input_blob,
    connect_output_blob,
    connect_artifact_blob,
    connect_status,
    connect_status_detail,
    connect_started_at,
    connect_finished_at,
    workflow_execution_name,
    // response_data,
  }) => ({
    id,
    connectId: connect_id,
    appConnectMasterId: app_connect_master_id,
    triggerType: trigger_type,
    connectJobId: connect_job_id,
    connectInputBlob: connect_input_blob,
    connectOutputBlob: connect_output_blob,
    connectArtifactBlob: connect_artifact_blob,
    connectStatus: connect_status,
    connectStatusDetail: connect_status_detail ?? null,
    connectStartedAt: connect_started_at,
    connectFinishedAt: connect_finished_at,
    workflowExecutionName: workflow_execution_name ?? null,
    // responseData: response_data,
  }),
};

export const { useItemsQuery: useConnectJobHistoriesQuery } =
  createFirestoreCollectionProsessor(processorOptions);
